'use client';
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';
import { HiMenu, HiX } from 'react-icons/hi';
import logo from '../assets/logo.png';
import Button1 from './ui/Button1';

const navLinks = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about&portfolio' },
  {
    name: 'Products & Services',
    subpages: [
      { name: 'SAM Robot', href: '/products&services/SAM-Robot' },
      { name: 'AgTron Workhorse', href: '/products&services/AgTron-Workhorse' },
      { name: 'AgTronGrow', href: '/products&services/AgTronGrow' },
      { name: 'Trading Platforms', href: '/products&services/Trading-Platforms' },
      { name: 'AI CV WebDev', href: '/products&services/AI-CV-WebDev' },
   
    ],
  },
  
  { name: 'Careers', href: '/careers' },
  { name: 'Invest', href: '/invest' },
];

export default function Header() {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <>
      {/* Navbar */}
      <header className="fixed top-0 left-0 z-50 w-full bg-[#f7f7f7]  ">
      
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <div onClick={()=>{navigate("/")}} className="text-2xl font-bold text-blue-700 ">
            <img src={logo} alt="logo" className="w-16 h-auto" />
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-12">
            {navLinks.map((link, index) =>
              link.subpages ? (
                <div
                  key={link.name}
                  className="relative group"
                  onMouseEnter={() => setOpenDropdown(index)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button className="flex items-center gap-1 text-gray-700  hover:text-blue-600 transition duration-200 font-medium">
                    {link.name}
                    <FaChevronDown className="text-sm mt-[1px]" />
                  </button>

                  <AnimatePresence>
                    {openDropdown === index && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 mt-2 w-40 rounded-lg bg-white shadow-lg "
                      >
                        <ul className="py-2">
                          {link.subpages.map((sublink) => (
                            <li key={sublink.name}>
                              <NavLink
                                to={sublink.href}
                                className={({ isActive }) =>
                                  `block px-4 py-2 text-sm ${
                                    isActive
                                      ? 'text-blue-600'
                                      : 'text-gray-700 '
                                  } hover:bg-gray-100 `
                                }
                              >
                                {sublink.name}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink
                  key={link.name}
                  to={link.href}
                  className={({ isActive }) =>
                    `relative font-medium transition-colors duration-200 ${
                      isActive
                        ? 'text-blue-600'
                        : 'text-gray-700  hover:text-blue-600'
                    }`
                  }
                >
                  {link.name}
                  {/* Active underline */}
                  {({ isActive }) =>
                    isActive && (
                      <motion.div
                        layoutId="underline"
                        className="absolute -bottom-1 left-0 h-[2px] w-full bg-blue-600"
                      />
                    )
                  }
                </NavLink>
              )
            )}
            
            {/* Contact Button */}
            <NavLink to="/contact">
              <Button1 width={"160px"}>
                Contact
              </Button1>
            </NavLink>
          </nav>

          {/* Mobile menu icon */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden text-3xl text-gray-700 "
          >
            <HiMenu />
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed top-0 right-0 h-full w-64 z-[999] bg-white  shadow-lg px-6 py-4"
          >
            <div className="flex justify-between items-center mb-6">
              <span className="text-xl font-bold text-blue-700 ">
                Menu
              </span>
              <button
                onClick={() => setSidebarOpen(false)}
                className="text-2xl text-gray-700 "
              >
                <HiX />
              </button>
            </div>
            <ul className="space-y-4">
              {navLinks.map((link) =>
                link.subpages ? (
                  <li key={link.name}>
                    <span className="block font-semibold text-gray-700 ">
                      {link.name}
                    </span>
                    <ul className="pl-4 mt-1 space-y-1">
                      {link.subpages.map((sublink) => (
                        <li key={sublink.name}>
                          <NavLink
                            to={sublink.href}
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) =>
                              `block text-sm ${
                                isActive
                                  ? 'text-jbsBlue'
                                  : 'text-gray-600 '
                              } hover:text-blue-600`
                            }
                          >
                            {sublink.name}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  <li key={link.name}>
                    <NavLink
                      to={link.href}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `block font-medium ${
                          isActive
                            ? 'text-jbsBlue'
                            : 'text-gray-700 '
                        } hover:text-blue-600`
                      }
                    >
                      {link.name}
                    </NavLink>
                  </li>
                )
              )}
              
              {/* Contact Button in Mobile Menu */}
              <li>
                <NavLink to="/contact" onClick={() => setSidebarOpen(false)}>
                  <Button1 width={"150px"}>
                    Contact
                  </Button1>
                </NavLink>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}